import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Field } from "../components/Field";
import { LakeCentreMapPicker } from "../components/LakeCentreMapPicker";

export default function AdminCanonicalLakeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: lake,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin_canonical_lake", id],
    queryFn: () =>
      fetch(`/app-api/admin/canonical-lakes/${encodeURIComponent(id!)}`).then(
        (r) => {
          if (!r.ok) throw new Error("Forbidden or not found");
          return r.json();
        },
      ),
  });

  const [form, setForm] = useState({
    canonical_name: "",
    venue_name_text: "",
    postcode: "",
    county_region: "",
    country: "",
    water_type: "",
    latitude: "",
    longitude: "",
    coordinate_method: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [mapPickSuccess, setMapPickSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!lake) return;
    setForm({
      canonical_name: lake.canonical_name ?? "",
      venue_name_text: lake.venue_name ?? "",
      postcode: lake.postcode ?? "",
      county_region: lake.county_region ?? "",
      country: lake.country ?? "",
      water_type: lake.water_type ?? "",
      latitude: lake.latitude != null ? String(lake.latitude) : "",
      longitude: lake.longitude != null ? String(lake.longitude) : "",
      coordinate_method: lake.coordinate_method ?? "",
    });
  }, [lake]);

  function handleMapPick(latitude: number, longitude: number) {
    setForm((f) => ({
      ...f,
      latitude: String(latitude),
      longitude: String(longitude),
      coordinate_method: "map_center",
    }));
    setMapPickSuccess(
      `Lake-centre coordinates selected: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
    );
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const payload: Record<string, unknown> = {
      canonical_name: form.canonical_name.trim() || undefined,
      venue_name_text: form.venue_name_text,
      postcode: form.postcode.trim() || undefined,
      county_region: form.county_region.trim() || undefined,
      country: form.country.trim() || undefined,
      water_type: form.water_type.trim() || undefined,
      coordinate_method: form.coordinate_method.trim() || undefined,
    };
    if (form.latitude.trim() !== "")
      payload.latitude = Number(form.latitude.trim());
    if (form.longitude.trim() !== "")
      payload.longitude = Number(form.longitude.trim());

    try {
      const res = await fetch(
        `/app-api/admin/canonical-lakes/${encodeURIComponent(id!)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save changes");
      setSaveSuccess(true);
      queryClient.invalidateQueries({ queryKey: ["admin_canonical_lake", id] });
      queryClient.invalidateQueries({ queryKey: ["admin_lake_reviews"] });
      queryClient.invalidateQueries({
        queryKey: ["admin_lake_review_detail", id],
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading)
    return (
      <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-secondary">
        Loading lake details…
      </div>
    );

  if (error || !lake)
    return (
      <div className="mx-auto max-w-[760px] px-4 py-8 text-center text-sm text-error">
        Lake not found or access denied.{" "}
        <Link to="/admin/lake-reviews" className="text-accent hover:underline">
          Back to reviews
        </Link>
      </div>
    );

  const backHref = `/admin/lake-reviews/${encodeURIComponent(id!)}`;
  const latitude =
    form.latitude.trim() === "" || !Number.isFinite(Number(form.latitude))
      ? null
      : Number(form.latitude);
  const longitude =
    form.longitude.trim() === "" || !Number.isFinite(Number(form.longitude))
      ? null
      : Number(form.longitude);

  return (
    <div className="mx-auto max-w-[760px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <nav className="flex items-center gap-1 text-sm text-secondary">
          <Link
            to="/admin/lake-reviews"
            className="text-accent hover:underline"
          >
            Lake Reviews
          </Link>
          <span className="mx-1">›</span>
          <Link to={backHref} className="text-accent hover:underline">
            {lake.canonical_name}
          </Link>
          <span className="mx-1">›</span>
          <span className="text-primary">Edit</span>
        </nav>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-primary">
          Edit lake details
        </h1>
        <p className="mt-1 text-sm text-secondary">
          Updates the canonical directory record in place. Does not change
          verification status, active listing, or any personal lake data.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${
            lake.verification_status === "verified"
              ? "bg-success-weak text-success"
              : lake.verification_status === "community_submitted"
                ? "bg-warning-weak text-warning"
                : "bg-inset text-secondary"
          }`}
        >
          {lake.verification_status ?? "unknown"}
        </span>
        {lake.active_listed ? (
          <span className="inline-flex items-center rounded-full bg-success-weak px-2 py-0.5 font-medium text-success">
            Listed
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-inset px-2 py-0.5 font-medium text-secondary">
            Not listed
          </span>
        )}
        <span className="inline-flex items-center rounded-full bg-inset px-2 py-0.5 font-medium text-secondary">
          ID: {lake.id}
        </span>
      </div>

      <form onSubmit={handleSave} className="space-y-5">
        <Field label="Lake name" id="cl-name">
          <Input
            id="cl-name"
            value={form.canonical_name}
            onChange={(e) =>
              setForm((f) => ({ ...f, canonical_name: e.target.value }))
            }
            placeholder="e.g. Gold Lake"
            required
          />
        </Field>

        <Field
          label="Venue / fishery"
          id="cl-venue"
          help="Must exactly match an existing venue name in the directory. Leave blank to remove the venue link."
        >
          <Input
            id="cl-venue"
            value={form.venue_name_text}
            onChange={(e) =>
              setForm((f) => ({ ...f, venue_name_text: e.target.value }))
            }
            placeholder="e.g. Gold Valley"
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Postcode" id="cl-postcode">
            <Input
              id="cl-postcode"
              value={form.postcode}
              onChange={(e) =>
                setForm((f) => ({ ...f, postcode: e.target.value }))
              }
              placeholder="e.g. GU11 2PT"
            />
          </Field>
          <Field label="County / region" id="cl-county">
            <Input
              id="cl-county"
              value={form.county_region}
              onChange={(e) =>
                setForm((f) => ({ ...f, county_region: e.target.value }))
              }
              placeholder="e.g. Hampshire"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Country" id="cl-country">
            <Input
              id="cl-country"
              value={form.country}
              onChange={(e) =>
                setForm((f) => ({ ...f, country: e.target.value }))
              }
              placeholder="e.g. France"
            />
          </Field>
          <Field label="Water type" id="cl-water-type">
            <Input
              id="cl-water-type"
              value={form.water_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, water_type: e.target.value }))
              }
              placeholder="e.g. stillwater"
            />
          </Field>
        </div>

        <Field
          label="Lake centre location"
          id="cl-map-centre"
          help="Use the free map to choose a representative point near the centre of the actual water. This coordinate powers weather and location intelligence."
        >
          <LakeCentreMapPicker
            latitude={latitude}
            longitude={longitude}
            country={form.country}
            onPick={handleMapPick}
          />
          {mapPickSuccess && (
            <p className="mt-2 text-sm text-success" role="status">
              {mapPickSuccess}
            </p>
          )}
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Latitude" id="cl-lat">
            <Input
              id="cl-lat"
              type="number"
              step="0.000001"
              value={form.latitude}
              onChange={(e) => {
                setMapPickSuccess(null);
                setForm((f) => ({
                  ...f,
                  latitude: e.target.value,
                  coordinate_method:
                    f.coordinate_method === "map_center"
                      ? "manual"
                      : f.coordinate_method,
                }));
              }}
              placeholder="46.227600"
            />
          </Field>
          <Field label="Longitude" id="cl-lng">
            <Input
              id="cl-lng"
              type="number"
              step="0.000001"
              value={form.longitude}
              onChange={(e) => {
                setMapPickSuccess(null);
                setForm((f) => ({
                  ...f,
                  longitude: e.target.value,
                  coordinate_method:
                    f.coordinate_method === "map_center"
                      ? "manual"
                      : f.coordinate_method,
                }));
              }}
              placeholder="2.213700"
            />
          </Field>
        </div>
        <p className="text-xs text-secondary">
          Coordinate method: {form.coordinate_method || "manual / existing"}
        </p>

        {saveError && (
          <p
            className="rounded-md bg-error-weak p-3 text-sm text-error"
            role="alert"
          >
            {saveError}
          </p>
        )}
        {saveSuccess && (
          <p
            className="rounded-md bg-success-weak p-3 text-sm text-success"
            role="status"
          >
            Lake details saved.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" isLoading={saving}>
            Save changes
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(backHref)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
